import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Divider from '@mui/material/Divider';
import SvgIcon from '@mui/material/SvgIcon';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import startCase from 'lodash/startCase';
import { BadgeStatus, BaseTooltip, BasePopover } from '@mgm/common/components/base';
import { dateFormatTZ } from '@mgm/common/utils';
import { useShopStatusAPI } from '../../../hooks/api/useClock';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getPersonnelStatus } from '../../../store/clock/selectors';
import { ReactComponent as UsersIcon } from '../../../static/icons/ic_users.svg';
import { useSheets } from '../../../hooks/useSheets';

const ITEM_HEIGHT = 64;
const PADDING_ITEM = 2.5;

const ContactsPopover = () => {
  const { t } = useTranslation('clock');
  const { mutate } = useShopStatusAPI();
  const personnelStatus = useAppSelector(getPersonnelStatus);
  const { getWorkTimes } = useSheets();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const openPopover = () => {
    setOpen(true);
    mutate();
  };

  return (
    <>
      <Box
        className="cursor-pointer"
        display="flex"
        alignItems="center"
        onClick={openPopover}
        ref={anchorRef}
      >
        <SvgIcon fontSize="large" component={UsersIcon} />
      </Box>
      <BasePopover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        sx={{ width: 360 }}
      >
        <Typography variant="h6" sx={{ p: PADDING_ITEM }}>
          {t('membersStatus')}
          <Typography pl={1} component="span">
            (
            {Object.keys(personnelStatus)?.length || 0}
            )
          </Typography>
        </Typography>
        <Divider sx={{ borderStyle: 'dashed' }} />
        <Box height={ITEM_HEIGHT * 6} overflow="auto">
          {Object.entries(personnelStatus).map(([email, value]) => {
            const { formattedBreak, formattedTime } = getWorkTimes(value);
            const inBreak = value?.status === 'inBreak';
            return (
              <ListItemButton
                disableGutters
                key={email}
                sx={{ px: PADDING_ITEM, height: ITEM_HEIGHT }}
              >
                <ListItemAvatar>
                  {value?.status !== 'inActive' ? (
                    <BaseTooltip tooltipText={(
                      <Stack>
                        <Typography variant="caption">
                          {t('clockedInAt')}
                          {' '}
                          {dateFormatTZ(value.shiftStart || '', 'hh:mm A')}
                          {' '}
                          {t('via')}
                          {' '}
                          {value.clockInVia}
                        </Typography>
                        <Typography variant="caption">
                          {inBreak ? formattedBreak : formattedTime}
                        </Typography>
                      </Stack>
                    )}
                    >
                      <BadgeStatus
                        status={value.status}
                        name={value.name}
                        avatar={value.avatar}
                      />
                    </BaseTooltip>
                  ) : (
                    <BadgeStatus
                      status={value.status}
                      name={value.name}
                      avatar={value.avatar}
                    />
                  )}
                </ListItemAvatar>
                <ListItemText
                  primaryTypographyProps={{ typography: 'subtitle2', mb: 0.25 }}
                  secondaryTypographyProps={{ typography: 'caption' }}
                  primary={startCase(value.name)}
                  secondary={value.location?.name}
                />
              </ListItemButton>
            );
          })}
        </Box>
      </BasePopover>
    </>
  );
};

export { ContactsPopover };
